import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from 'src/app/core/services/auth.service';
import { UserProfileService } from 'src/app/core/services/user-profile.service';

function passwordMatchValidator(): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const pwd = group.get('password')?.value;
    const confirm = group.get('confirmPassword')?.value;
    if (pwd && confirm && pwd !== confirm) {
      return { passwordMismatch: true };
    }
    return null;
  };
}

function passwordPolicyValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const v: string = control.value ?? '';
    if (!v) return null;
    const errors: ValidationErrors = {};
    if (v.length < 10) errors['minLength'] = true;
    if (!/[A-Z]/.test(v)) errors['missingUpper'] = true;
    if (!/[a-z]/.test(v)) errors['missingLower'] = true;
    if (!/\d/.test(v)) errors['missingDigit'] = true;
    if (!/[^A-Za-z0-9]/.test(v)) errors['missingSymbol'] = true;
    return Object.keys(errors).length ? { passwordPolicy: errors } : null;
  };
}

/**
 * Modal that prompts a magic-link user to set an initial password the first
 * time they land on the dashboard. The dialog is non-dismissable (no close
 * button, no escape, no outside-click) so the user must complete it.
 *
 * Usage (in a parent component template):
 *   <app-first-time-set-password
 *     [visible]="showSetPassword"
 *     (done)="onPasswordSet()"></app-first-time-set-password>
 *
 * The parent should set `visible = true` when the user profile reports
 * `hasPassword = false`, and re-fetch the profile when `done` fires.
 */
@Component({
  standalone: false,
  selector: 'app-first-time-set-password',
  templateUrl: './first-time-set-password.component.html',
  styleUrls: ['./first-time-set-password.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FirstTimeSetPasswordComponent {
  /**
   * Local mirror of the parent's `visible` input used as a two-way binding
   * target for the PrimeNG dialog. One-way `[visible]` doesn't work for the
   * X button because PrimeNG needs to mutate visibility when the user
   * dismisses; two-way `[(visible)]` on a local property is the pattern.
   */
  innerVisible = false;

  @Input()
  set visible(v: boolean) {
    this.innerVisible = !!v;
  }
  get visible(): boolean {
    return this.innerVisible;
  }

  @Output() done = new EventEmitter<void>();

  /**
   * @description Dialog-dismissed handler: fires after PrimeNG hides the
   * dialog (X button, Escape, or after submit). Notifies the parent so it
   * can flip its own show-flag. Profile cache is NOT cleared here — the
   * user chose to skip, so the next dashboard visit should re-prompt them.
   */
  onDismiss(): void {
    this.form.reset();
    this.serverError = null;
    this.done.emit();
  }

  form: FormGroup;
  isSubmitting = false;
  serverError: string | null = null;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private messageService: MessageService,
    private translate: TranslateService,
    private userProfileService: UserProfileService
  ) {
    this.form = this.fb.group(
      {
        password: ['', [Validators.required, passwordPolicyValidator()]],
        confirmPassword: ['', [Validators.required]],
      },
      { validators: passwordMatchValidator() }
    );
  }

  get passwordValue(): string {
    return (this.form.get('password')?.value as string) ?? '';
  }

  submit(): void {
    if (this.form.invalid || this.isSubmitting) return;
    this.isSubmitting = true;
    this.serverError = null;
    const newPassword: string = this.form.value.password;

    this.authService.setPassword(newPassword).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.messageService.add({
          severity: 'success',
          detail: this.translate.instant('auth.firstTimeSetPassword.successToast'),
          life: 2000,
        });
        this.form.reset();
        // Bust the profile cache so the updated hasPassword=true value is
        // visible to any consumer that re-reads the profile (dashboard guard,
        // settings tabs, etc.). Without this, the in-memory BehaviorSubject
        // still holds the old hasPassword=false and the popup re-opens next
        // time the dashboard loads.
        this.userProfileService.clearProfile();
        // Programmatically close the dialog (two-way visible binding) and
        // notify the parent so it also flips its show-flag.
        this.innerVisible = false;
        this.done.emit();
      },
      error: (err: HttpErrorResponse) => {
        this.isSubmitting = false;
        const code = err?.error?.error as string | undefined;
        if (code === 'auth.password.policy_violation') {
          this.serverError = this.translate.instant('auth.firstTimeSetPassword.policyError');
        } else {
          this.serverError = this.translate.instant('auth.firstTimeSetPassword.genericError');
        }
      },
    });
  }
}
